import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormLabel,
	Grid,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { paths } from '@/app/routes';
import { LoadDesignJsonDialog } from '@/components/Drawers/AddUnlayerEmailDrawer/dialogs/LoadDesignJsonDialog';
import ShareDialog from '@/components/dialogs/ShareDialog';
import SearchField from '@/components/SearchField';
import { OwnerMultiSelect } from '@/components/selects/OwnerSelect';
import { FUSE_OPTIONS } from '@/features/templates/helper';
import { formatDateTime } from '../helpers';
import { AiTemplateActiveTableCell } from '../AiTemplateActiveStatus';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import DataGridMultiBrokerGroup from '@/components/table/DataGridComponents/DataGridMultiBrokerGroup';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { NoRowsOverlay } from '@/components/table/DataGridComponents/NoRowsOverlay';
import DataGridColumnMenu from '@/components/table/DataGridComponents/DataGridColumnMenu';
import DataGridColumnReorderToolbar from '@/components/table/DataGridComponents/DataGridColumnReorderToolbar';
import { useUnlayerTemplatesEditorContext } from './UnlayerTemplatesEditorContext';
import RenderCreatorCell from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/UnlayerTemplatesEditorView/RenderCreatorCell';
import EditorDrawer from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/UnlayerTemplatesEditorView/EditorDrawer';
import DeleteDialog from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/UnlayerTemplatesEditorView/DeleteDialog';
import SaveAsDialog from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/UnlayerTemplatesEditorView/SaveAsDialog';
import DiscardDialog from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/UnlayerTemplatesEditorView/DiscardDialog';
import { AI_WORKFLOW_TYPES } from '@/features/aiWorkflow/constants';
import useCellModes from '@/hooks/useCellModes';
import { showToast } from '@/features/toast/toastSlice';
import Fuse from 'fuse.js';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';

const getWorkflowType = (v) => AI_WORKFLOW_TYPES[v.workflowType] || 'None';
const workflowSortComparator = (a, b) => getWorkflowType(a).localeCompare(getWorkflowType(b));
const EDITABLE_ROW_FIELDS = ['workflowType'];

const AI_TEMPLATE_HIDDEN_COLUMNS = ['active', 'sharedWith', 'workflowType'];

/**
 * Shared Unlayer template editor UI: sortable table, create/edit drawer, delete/save-as
 * dialogs, and optional AI-only fields for active status and broker-group sharing.
 */
export const UnlayerTemplatesEditorView = ({ redirectWhenDisabled = false }) => {
	const { labels } = useUnlayerTemplatesEditorContext();
	const showAiTemplateFields = Boolean(labels.showAiTemplateFields);

	return (
		<ColumnControlContextProvider
			variant='unlayerTemplates'
			value={showAiTemplateFields ? 'ai' : 'standard'}
			defaultHiddenColumns={showAiTemplateFields ? [] : AI_TEMPLATE_HIDDEN_COLUMNS}
		>
			<UnlayerTemplatesEditorViewContent redirectWhenDisabled={redirectWhenDisabled} />
		</ColumnControlContextProvider>
	);
};

const UnlayerTemplatesEditorViewContent = ({ redirectWhenDisabled = false }) => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const {
		labels,
		isSuccess,
		isFetching,
		showUnlayerEmails,
		listState,
		dialogs,
		isFetchingTemplates,
		isCreatingTemplate,
		isUpdatingTemplate,
		isDeletingTemplate,
		isSharingTemplate = false,
		sortedTemplates,
		showShareDialog = false,
		isFolioAdmin = false,
		canModifyTemplateRow = () => true,
		requestCreateTemplate,
		requestOpenTemplate,
		handleToggleTemplateActive,
		handleOpenShareDialog,
		handleShareTemplate,
		handleCloseLoadDesignDialog,
		handleLoadDesignJson,
		handleLoadDesignJsonValueChange,
		handleSort,
		handleUpdateTemplateProperties,
	} = useUnlayerTemplatesEditorContext();

	const { sortField, sortDirection } = listState;
	const loadDesignJson = dialogs.loadDesignJson;

	const apiRef = useGridApiRef();
	const { cellModesModel, handleCellClick, handleCellModesModelChange } = useCellModes();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const showAiTemplateFields = Boolean(labels.showAiTemplateFields);
	const showShareDialogOpen = useSelector((state) => state.dialog.open?.share);

	const [searchValue, setSearchValue] = useState('');
	const [ownerDocIds, setOwnerDocIds] = useState([]);

	const searchFuse = useMemo(() => {
		const fuseIndex = Fuse.createIndex(FUSE_OPTIONS.keys, sortedTemplates);
		return new Fuse(sortedTemplates, FUSE_OPTIONS, fuseIndex);
	}, [sortedTemplates]);

	const searchedPages = useMemo(() => {
		return searchFuse.search(searchValue).flatMap((v) => v?.item);
	}, [searchFuse, searchValue]);

	const rows = useMemo(() => {
		const base = (searchValue ? searchedPages : sortedTemplates) ?? [];
		if (!ownerDocIds?.length) {
			return base.slice();
		}
		const selectedDocIds = new Set(ownerDocIds.map((owner) => String(owner?.id)).filter(Boolean));
		return base.filter(
			(row) => row?.ownerDocId != null && selectedDocIds.has(String(row.ownerDocId)),
		);
	}, [ownerDocIds, searchValue, searchedPages, sortedTemplates]);

	const isSaving =
		isCreatingTemplate || isUpdatingTemplate || isDeletingTemplate || isSharingTemplate;

	const columns = useMemo(
		() =>
			[
				{
					field: 'name',
					headerName: 'Template name',
					display: 'flex',
					flex: 2,
					minWidth: 200,
					hideable: false,
					renderCell: ({ row }) => row.name ?? labels.untitledTemplate,
				},
				{
					field: 'subject',
					headerName: 'Subject',
					display: 'flex',
					flex: 2,
					minWidth: 200,
					renderCell: ({ row }) => row.subject ?? '-',
				},
				{
					field: 'workflowType',
					headerName: 'Template type',
					display: 'flex',
					flex: 2,
					minWidth: 200,
					editable: true,
					singleClickCell: true,
					type: 'singleSelect',
					sortComparator: workflowSortComparator,
					renderCell: ({ row }) => getWorkflowType(row),
					valueOptions: () => [
						{ value: 'None', label: 'None' },
						...Object.entries(AI_WORKFLOW_TYPES).map(([key, title]) => ({
							value: key,
							label: title,
						})),
					],
				},
				{
					field: 'createdBy',
					headerName: 'Created by',
					display: 'flex',
					flex: 1,
					minWidth: 150,
					renderCell: ({ row }) => <RenderCreatorCell {...{ row }} />,
				},
				{
					field: 'active',
					headerName: labels.activeColumnLabel,
					display: 'flex',
					flex: 1,
					maxWidth: 100,
					minWidth: 100,
					hideable: false,
					reorderable: false,
					renderCell: ({ row }) => (
						<AiTemplateActiveTableCell
							template={row}
							labels={labels}
							disabled={isSaving || !canModifyTemplateRow(row)}
							onToggleActive={(nextActive) =>
								handleToggleTemplateActive(row.id, nextActive)
							}
						/>
					),
				},
				{
					field: 'sharedWith',
					headerName: labels.sharedWithColumnLabel,
					display: 'flex',
					sortable: false,
					flex: 2,
					minWidth: 200,
					renderCell: ({ row }) => {
						const canShare = canModifyTemplateRow(row);
						if (row.master) {
							return <Typography variant='body2'>-</Typography>;
						}
						if (!row.brokerGroupIds?.length) {
							return (
								<Box
									onClick={
										canShare ? () => handleOpenShareDialog(row) : undefined
									}
									sx={{
										width: '100%',
										height: '100%',
										alignContent: 'center',
										cursor: canShare ? 'pointer' : 'default',
									}}
								>
									<Typography variant='inherit' color='text.secondary'>
										{'Not shared'}
									</Typography>
								</Box>
							);
						}
						return (
							<DataGridMultiBrokerGroup
								brokerGroupIds={row.brokerGroupIds ?? []}
								showAdmin={userDetails?.isFolioAdmin}
								onClick={canShare ? () => handleOpenShareDialog(row) : undefined}
								sx={{ cursor: canShare ? 'pointer' : 'default' }}
							/>
						);
					},
				},
				{
					field: 'createdWhen',
					headerName: 'Created date',
					display: 'flex',
					minWidth: 150,
					renderCell: ({ row }) => formatDateTime(row.createdWhen),
				},
				{
					field: 'lastUpdated',
					headerName: 'Last updated',
					display: 'flex',
					minWidth: 150,
					renderCell: ({ row }) => formatDateTime(row.lastUpdated),
				},
			]
				.map((column) => ({ ...column, disableColumnMenu: column.hideable === false }))
				.filter(
					(column) =>
						showAiTemplateFields || !AI_TEMPLATE_HIDDEN_COLUMNS.includes(column.field),
				),
		[
			canModifyTemplateRow,
			handleOpenShareDialog,
			handleToggleTemplateActive,
			isSaving,
			labels,
			showAiTemplateFields,
			userDetails?.isFolioAdmin,
		],
	);

	const {
		columns: orderedColumns,
		columnVisibilityModel,
		onColumnVisibilityModelChange,
		onColumnWidthChange,
	} = useDataGridColumnProps(columns);

	const sortModel = useMemo(
		() => [
			{
				field: sortField,
				sort: sortDirection,
			},
		],
		[sortField, sortDirection],
	);

	if (redirectWhenDisabled && isSuccess && !showUnlayerEmails) {
		return <Navigate to={paths.home} replace />;
	}

	const handleRowClick = (params, event) => {
		const cell = event.target.closest('.MuiDataGrid-cell');
		const field = cell?.dataset?.field;

		if (
			field === 'sharedWith' ||
			field === 'workflowType' ||
			event.target.closest(
				'button, a[href], .MuiLink-root, .MuiSwitch-root, .has-link, .MuiCheckbox-root, input, [role="button"]',
			)
		) {
			return;
		}
		const { row } = params;
		requestOpenTemplate(row.id);
	};

	const handleProcessRowUpdate = async (newRow, oldRow) => {
		if (oldRow.master || !canModifyTemplateRow(oldRow)) {
			return oldRow;
		}

		const properties = Object.entries(newRow).reduce((acc, [key, value]) => {
			if (EDITABLE_ROW_FIELDS.includes(key) && oldRow[key] !== value) {
				acc[key] = key === 'workflowType' && value === 'None' ? '' : value;
			}
			return acc;
		}, {});

		if (Object.keys(properties).length === 0) {
			return oldRow;
		}

		await handleUpdateTemplateProperties({
			id: oldRow.id,
			properties,
		});

		return {
			...newRow,
			...properties,
		};
	};

	return (
		<>
			<Stack spacing={2}>
				<Stack direction='row' spacing={1} sx={{ alignItems: 'flex-end' }}>
					<Grid
						container
						spacing={2}
						alignItems='flex-end'
						sx={{
							'& .MuiInputBase-root': {
								height: 40,
								minHeight: 40,
							},
							'& .MuiOutlinedInput-input': {
								padding: '8px',
							},
							flex: 1,
							'& .MuiFormControl-root': {
								width: '100%',
							},
						}}
					>
						<Grid size={{ xs: 12, sm: 4 }}>
							<FormControl fullWidth>
								<FormLabel>Search</FormLabel>
								<SearchField
									value={searchValue}
									onChange={(event) => setSearchValue(event.target.value)}
									onReset={() => setSearchValue('')}
									helperText=''
								/>
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, sm: 2 }}>
							<FormControl fullWidth>
								<FormLabel>Created by</FormLabel>
								<OwnerMultiSelect
									users={ownerDocIds}
									onChange={setOwnerDocIds}
									returnValue='object'
								/>
							</FormControl>
						</Grid>
					</Grid>
					<Box pb={0.5}>
						<Button variant='contained' onClick={requestCreateTemplate}>
							{labels.createButton}
						</Button>
					</Box>
				</Stack>
				<Box
					position='relative'
					sx={{
						height: '75vh',
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: '8px',
						backgroundColor: theme.palette.background.paper,
					}}
				>
					<DataGrid
						apiRef={apiRef}
						sx={{
							'& .MuiDataGrid-editInputCell': {
								backgroundColor: 'transparent',
							},
							'--DataGrid-overlayHeight': '3em',
							'& .MuiDataGrid-cell': {
								padding: 1,
								minHeight: '42px',
								cursor: 'pointer',
							},
							'& .MuiDataGrid-row.Mui-selected': {
								color: 'unset',
							},
							border: 'none',
							borderRadius: '8px',
						}}
						loading={isFetchingTemplates && sortedTemplates.length === 0}
						rows={rows}
						columns={orderedColumns}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={onColumnVisibilityModelChange}
						onColumnWidthChange={onColumnWidthChange}
						width='100%'
						getRowHeight={() => 'auto'}
						isCellEditable={(params) =>
							!params?.row?.master &&
							params.colDef.editable &&
							canModifyTemplateRow(params.row)
						}
						disableRowSelectionOnClick
						onRowClick={handleRowClick}
						{...(showAiTemplateFields && {
							processRowUpdate: handleProcessRowUpdate,
							onProcessRowUpdateError: (error) =>
								dispatch(
									showToast({
										message:
											error?.data?.message ?? 'Unable to update template',
										error: true,
									}),
								),
						})}
						cellModesModel={cellModesModel}
						onCellModesModelChange={handleCellModesModelChange}
						onCellClick={handleCellClick}
						sortingOrder={['desc', 'asc']}
						sortModel={sortModel}
						onSortModelChange={(newModel) =>
							handleSort(newModel?.[0]?.field ?? sortField)
						}
						slots={{
							noRowsOverlay: () => (
								<NoRowsOverlay
									label={
										isFetchingTemplates
											? labels.loadingTemplates
											: labels.emptyTemplates
									}
								/>
							),
							toolbar: DataGridColumnReorderToolbar,
							columnMenu: DataGridColumnMenu,
						}}
						disableColumnFilter
					/>
				</Box>
			</Stack>

			<EditorDrawer />
			<DeleteDialog />
			<LoadDesignJsonDialog
				loadDesignJsonError={loadDesignJson.error}
				loadDesignJsonValue={loadDesignJson.value}
				onClose={handleCloseLoadDesignDialog}
				onLoad={handleLoadDesignJson}
				onValueChange={handleLoadDesignJsonValueChange}
				open={loadDesignJson.open}
			/>
			<SaveAsDialog />
			<DiscardDialog />

			{showShareDialog && showShareDialogOpen && (
				<ShareDialog
					title='AI Template'
					hideUsers={true}
					showAdmin={isFolioAdmin}
					config={{ brokerGroup: { groupBy: 'ledger' } }}
					onConfirm={handleShareTemplate}
				/>
			)}

			{(!isSuccess || isFetching) && (
				<Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
					<CircularProgress size={24} />
				</Box>
			)}
		</>
	);
};
